import { Download, File as FileIcon, Film, Image as ImageIcon, Loader2 } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { ImagePreviewModal } from '@/components/common/image-preview-modal'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { generalChatService } from '@/services/general-chat.service'

import { Conversation, Message, MessageType } from '@/types/chat.type'

interface ConversationMediaDialogProps {
  conversation: Conversation
  onClose: () => void
}

type MediaTab = 'image' | 'video' | 'file'

const FETCH_LIMIT = 100
const VIDEO_FILE_EXTENSIONS = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.m4v']

const formatBytes = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const hasVideoFileExtension = (fileName?: string): boolean => {
  if (!fileName) return false
  const lowerFileName = fileName.toLowerCase()
  return VIDEO_FILE_EXTENSIONS.some(extension => lowerFileName.endsWith(extension))
}

const isVideoMessage = (msg: Message): boolean => {
  if (msg.type === MessageType.VIDEO) return true
  if (msg.type === MessageType.FILE && msg.fileType?.startsWith('video/')) return true
  if (msg.type === MessageType.FILE && hasVideoFileExtension(msg.fileName)) return true
  return false
}

const isImageMessage = (msg: Message): boolean => msg.type === MessageType.IMAGE

const isOtherFileMessage = (msg: Message): boolean => {
  if (msg.type !== MessageType.FILE) return false
  return !msg.fileType?.startsWith('video/')
}

export const ConversationMediaDialog: React.FC<ConversationMediaDialogProps> = ({ conversation, onClose }) => {
  const [messages, setMessages] = React.useState<Message[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [currentTab, setCurrentTab] = React.useState<MediaTab>('image')
  const [previewImage, setPreviewImage] = React.useState<{ src: string; alt: string } | null>(null)

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await generalChatService.getMessages(conversation.id, {
          limit: FETCH_LIMIT,
          types: [MessageType.IMAGE, MessageType.FILE, MessageType.VIDEO],
        })
        if (!cancelled) setMessages(response.data)
      } catch (e) {
        if (!cancelled) setError('Failed to load media. Please try again.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [conversation.id])

  const images = React.useMemo(() => messages.filter(isImageMessage), [messages])
  const videos = React.useMemo(() => messages.filter(isVideoMessage), [messages])
  const files = React.useMemo(() => messages.filter(isOtherFileMessage), [messages])

  const tabs: Array<{ key: MediaTab; label: string; Icon: React.ComponentType<{ className?: string }>; count: number }> = [
    { key: 'image', label: 'Images', Icon: ImageIcon, count: images.length },
    { key: 'video', label: 'Videos', Icon: Film, count: videos.length },
    { key: 'file', label: 'Files', Icon: FileIcon, count: files.length },
  ]

  const handleDownload = async (url: string, name?: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = name || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch {
      window.open(url, '_blank')
    }
  }

  const renderEmpty = (label: string) => (
    <div className="text-center py-12 text-gray-500">
      <p className="text-sm">No {label} shared in this conversation yet.</p>
    </div>
  )

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Shared media</DialogTitle>
          </DialogHeader>

          <div className="flex items-stretch border-b border-gray-100">
            {tabs.map(({ key, label, Icon, count }) => {
              const isActive = currentTab === key
              return (
                <button
                  key={key}
                  onClick={() => setCurrentTab(key)}
                  className={`group relative flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                    isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'scale-110' : ''} transition-transform duration-200`} />
                  <span>{label}</span>
                  <span
                    className={`text-xs ${
                      isActive ? 'text-blue-600' : 'text-gray-400'
                    }`}
                  >
                    {count}
                  </span>
                  <span
                    className={`pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-blue-600 transition-all duration-300 ${
                      isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
                    }`}
                  />
                </button>
              )
            })}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-sm text-red-600">{error}</div>
            ) : currentTab === 'image' ? (
              images.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-1">
                  {images.map(msg => (
                    <button
                      key={msg.id}
                      onClick={() => setPreviewImage({ src: msg.fileUrl || '', alt: msg.fileName || 'Shared image' })}
                      className="group relative aspect-square overflow-hidden rounded-md bg-gray-100 hover:opacity-90 transition-opacity"
                    >
                      {msg.fileUrl && (
                        <Image
                          src={msg.fileUrl}
                          alt={msg.fileName || 'Shared image'}
                          fill
                          unoptimized
                          className="object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                renderEmpty('images')
              )
            ) : currentTab === 'video' ? (
              videos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-1">
                  {videos.map(msg => (
                    <div key={msg.id} className="rounded-lg overflow-hidden border border-gray-100 bg-black">
                      {msg.fileUrl && (
                        <video src={msg.fileUrl} controls className="w-full h-auto max-h-64 bg-black" preload="metadata" />
                      )}
                      <div className="flex items-center justify-between px-3 py-2 bg-white">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{msg.fileName || 'Video'}</p>
                          {msg.fileSize && <p className="text-xs text-gray-500">{formatBytes(msg.fileSize)}</p>}
                        </div>
                        {msg.fileUrl && (
                          <button
                            onClick={() => {
                              if (msg.fileUrl) {
                                handleDownload(msg.fileUrl, msg.fileName)
                              }
                            }}
                            className="p-1.5 text-gray-500 hover:text-gray-900 rounded-md hover:bg-gray-100"
                            aria-label="Download video"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderEmpty('videos')
              )
            ) : files.length > 0 ? (
              <div className="space-y-2 p-1">
                {files.map(msg => (
                  <div
                    key={msg.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                  >
                    <div className="h-9 w-9 flex items-center justify-center rounded-md bg-white text-blue-600 flex-shrink-0">
                      <FileIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{msg.fileName || 'File'}</p>
                      <p className="text-xs text-gray-500">
                        {[msg.fileType, formatBytes(msg.fileSize)].filter(Boolean).join(' • ')}
                      </p>
                    </div>
                    {msg.fileUrl && (
                      <button
                        onClick={() => {
                          if (msg.fileUrl) {
                            handleDownload(msg.fileUrl, msg.fileName)
                          }
                        }}
                        className="p-2 text-gray-500 hover:text-gray-900 rounded-md hover:bg-gray-100 flex-shrink-0"
                        aria-label="Download file"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              renderEmpty('files')
            )}
          </div>
        </DialogContent>
      </Dialog>

      {previewImage && (
        <ImagePreviewModal
          src={previewImage.src}
          alt={previewImage.alt}
          isOpen={!!previewImage}
          onClose={() => setPreviewImage(null)}
          showDownload
        />
      )}
    </>
  )
}
